'use client';
import React, { useEffect, useRef, useState } from 'react';
import icFavorite from '@/../public/assets/ic-favorite.svg';
import icTc from '@/../public/assets/ic-tc.svg';
import icLogout from '@/../public/assets/ic-logout.svg';
import { useTranslations } from 'next-intl';
import CWProfileMenuItem from '@/context/global/components/g00/g00-d00-g008-cw-profile-menu-item';
import { getMemberTierLabel } from '@/context/global/utils/getMemberTierLabel';
import { redirect, RedirectType } from 'next/navigation';
import { CWLogoutConfirm } from '@/context/global/components/g00/g00-d00-g012-cw-logout-confirm';

type MenuItem = {
  id: 'favorite' | 'terms' | 'policy' | 'logout';
  labelKey: string;
  icon: string;
  type: 'link' | 'action';
  href?: string;
  action?: () => void;
  showChevron?: boolean;
};

export type ProfileDropdownProps = {
  avatarUrl: string;
  avatarBg?: string | null;
  aliasName?: string;
  badge?: number;
  onClose: () => void;
};

export default function CWProfileDropdown({
  avatarUrl,
  avatarBg,
  aliasName,
  badge,
  onClose,
}: ProfileDropdownProps) {
  const t = useTranslations();
  const [visible, setVisible] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogoutClick = () => {
    setVisible(false);
    setShowLogoutConfirm(true);
  };

  const menuItems: MenuItem[] = [
    {
      id: 'favorite',
      labelKey: 'ProfileDropdown.favoriteSongs',
      icon: icFavorite,
      type: 'link',
      href: '/favorites',
      showChevron: true,
    },
    {
      id: 'terms',
      labelKey: 'ProfileDropdown.terms',
      icon: icTc,
      type: 'link',
      href: '/terms',
      showChevron: true,
    },
    {
      id: 'policy',
      labelKey: 'ProfileDropdown.policy',
      icon: icTc,
      type: 'link',
      href: '/policy',
      showChevron: true,
    },
    {
      id: 'logout',
      labelKey: 'ProfileDropdown.logout',
      icon: icLogout,
      type: 'action',
      action: handleLogoutClick,
      showChevron: false,
    },
  ];

  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setTimeout(() => setVisible(true), 10);
    const handleClickOutside = (event: MouseEvent) => {
      if (showLogoutConfirm) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setVisible(false);
        setTimeout(onClose, 200);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [onClose, showLogoutConfirm]);

  return (
    <>
      <div
        ref={dropdownRef}
        className={`absolute right-0 z-50 mt-2 max-w-[340px] overflow-hidden rounded-[24px] bg-[#222] shadow-[0_4px_32px_0_rgba(22,23,25,0.15)] transition-all duration-200 sm:w-[340px] ${visible ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'}`}
      >
        {/* Header */}
        <div
          className="relative cursor-pointer px-6 pb-6 pt-6"
          onClick={() => redirect('/edit-profile', RedirectType.push)}
          style={{
            background:
              'linear-gradient(315deg, #B2D233 14.8%, #A3C937 23.13%, #8CBC3F 37.96%, #8EBC43 46.29%, #94BE51 56.49%, #9FC169 67.6%, #AEC589 78.72%, #C1CAB3 89.84%, #CECECE 96.32%)',
          }}
        >
          <div className="pointer-events-none absolute bottom-0 right-0 h-20 w-32 select-none opacity-20">
            <svg viewBox="0 0 120 60" fill="none" className="h-full w-full">
              <text
                x="10"
                y="40"
                fontSize="32"
                fill="#B6E34A"
                fontWeight="bold"
                opacity="0.3"
              >
                PRO
              </text>
            </svg>
          </div>
          <div className="relative z-10 flex items-center gap-4">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white shadow"
              style={avatarBg ? { background: avatarBg } : { background: '#fff' }}
            >
              <img
                src={avatarUrl}
                alt="User Avatar"
                className="h-12 w-12 rounded-full object-cover"
              />
            </div>
            <div>
              <div className="mb-[-6] text-[24px] font-bold leading-tight text-[#030606]">
                {aliasName ?? '-'}
              </div>
              <div className="text-[20px] text-[#030606]">
                {getMemberTierLabel(badge)}
              </div>
            </div>
          </div>
        </div>

        {/* Menu */}
        {menuItems.map((item, index) => (
          <React.Fragment key={item.id}>
            {index > 0 && <div className="mx-6 border-t border-[#333]" />}
            <CWProfileMenuItem
              label={t(item.labelKey)}
              icon={item.icon}
              href={item.type === 'link' ? item.href : undefined}
              action={item.type === 'action' ? item.action : undefined}
              showChevron={item.showChevron}
              onClose={onClose}
            />
          </React.Fragment>
        ))}
      </div>

      <CWLogoutConfirm
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={onClose}
      />
    </>
  );
}
